import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import type { BoxProps } from '@/components/Box';
import type { FlexProps } from '@/components/Flex';
import { Flex } from '@/components/Flex';
import * as S from '@/components/StickyLayout.styles';
import type { WithChildren } from '@/lib/types';
import { useConfigProvider } from '@/providers/ConfigProvider';

export type StickyLayoutProps = WithChildren &
  Pick<FlexProps, 'backgroundColor'> & {
    style?: CSSProperties;
  };

const StickyLayout = ({ children, style, ...props }: StickyLayoutProps) => (
  <Flex
    data-test-id='StickyLayout'
    flexDirection='column'
    justifyContent='space-between'
    style={{ minHeight: '100vh', maxWidth: '100vw', ...style }}
    {...props}
  >
    {children}
  </Flex>
);

type MainProps = WithChildren &
  Pick<BoxProps, 'backgroundColor'> & { headerBackground?: string };

const Main = ({ children, ...props }: MainProps) => {
  const { headerRef } = useConfigProvider();
  const [headerHeight, setHeaderHeight] = useState(0);

  useEffect(() => {
    if (props.headerBackground?.toLowerCase()?.includes('transparent')) {
      setHeaderHeight(0);
      return;
    }
    if (!headerRef?.current) return;

    const observer = new ResizeObserver(() => {
      setHeaderHeight(headerRef.current?.offsetHeight || 0);
    });

    observer.observe(headerRef.current);

    return () => observer.disconnect();
  }, [headerRef]);

  return (
    <S.MainBox
      $headerHeight={headerHeight}
      as='main'
      data-test-id='StickyLayoutMain'
      id='main-content'
      {...props}
      style={{ flex: 1 }}
    >
      {children}
    </S.MainBox>
  );
};

StickyLayout.Main = Main;

export { StickyLayout };
